import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Check, DeviceMobile, QrCode, Phone } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function UpiPayModal({ open, onOpenChange, plan, onSubmitted }) {
  const [info, setInfo] = useState(null);
  const [txn, setTxn] = useState("");
  const [payer, setPayer] = useState("");
  const [note, setNote] = useState("");
  const [copied, setCopied] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get("/payments/upi/info");
      setInfo(data.upi);
    })();
  }, [open]);

  const copy = async (v, key) => {
    try {
      await navigator.clipboard.writeText(v);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!txn.trim()) { toast.error("Enter UPI transaction reference"); return; }
    setSubmitting(true);
    try {
      await api.post("/payments/upi/claim", {
        plan: plan.id,
        transaction_ref: txn.trim(),
        payer_name: payer.trim() || null,
        note: note.trim() || null,
      });
      toast.success("Payment claim submitted. Admin will verify within 24 hours.");
      setTxn(""); setPayer(""); setNote("");
      onSubmitted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const upiDeeplink = info ? `upi://pay?pa=${encodeURIComponent(info.upi_id)}&pn=${encodeURIComponent(info.payee_name || "Rashid Hussain")}&am=${plan?.price || ""}&cu=INR&tn=${encodeURIComponent(`WR Rishtey ${plan?.name || ""}`)}` : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white p-0 gap-0 border-brand-line" data-testid="upi-modal">
        <DialogHeader className="p-8 pb-4 border-b border-brand-line">
          <DialogTitle className="font-serif text-3xl tracking-tight text-brand-ink">
            Pay via UPI / G-Pay
          </DialogTitle>
          <DialogDescription className="text-brand-muted">
            {plan ? <>Send <span className="font-semibold text-brand-ink">₹{plan.price}</span> for the <span className="font-semibold text-brand-ink">{plan.name}</span> membership, then paste the UPI transaction reference below.</> : "Loading…"}
          </DialogDescription>
        </DialogHeader>

        {info && (
          <div className="grid md:grid-cols-2">
            {/* QR + details */}
            <div className="p-8 border-r border-brand-line bg-brand-bone/60">
              <div className="label-eyebrow text-brand-terracotta flex items-center gap-1">
                <QrCode size={12} /> Scan with any UPI app
              </div>
              <div className="mt-4 bg-white p-3 border border-brand-line inline-block text-brand-muted text-xs uppercase tracking-widest text-center">
                <div className="w-64 h-32 flex items-center justify-center">
                  Enter UPI ID manually in your bank app
                </div>
              </div>
              <div className="mt-4 text-sm">
                <div className="text-brand-muted">Paid to</div>
                <div className="font-serif text-xl text-brand-ink">{info.payee_name}</div>
                <div className="text-xs text-brand-muted mt-1">Axis Bank</div>
              </div>
              <a
                href={upiDeeplink}
                data-testid="upi-deeplink-btn"
                className="mt-5 inline-flex items-center gap-2 text-sm text-brand-terracotta underline underline-offset-4"
              >
                <DeviceMobile size={14} /> Open in UPI app (mobile)
              </a>
            </div>

            {/* Contact details + form */}
            <div className="p-8 flex flex-col">
              <div className="space-y-3">
                <CopyRow
                  label="UPI ID"
                  value={info.upi_id}
                  copied={copied === "upi"}
                  onCopy={() => copy(info.upi_id, "upi")}
                  testid="copy-upi-id"
                />
                <CopyRow
                  label="Phone / G-Pay"
                  value={info.phone}
                  copied={copied === "phone"}
                  onCopy={() => copy(info.phone, "phone")}
                  icon={Phone}
                  testid="copy-phone"
                />
                <CopyRow
                  label="Amount"
                  value={plan ? `₹${plan.price}` : ""}
                  copied={copied === "amt"}
                  onCopy={() => plan && copy(String(plan.price), "amt")}
                  testid="copy-amount"
                />
              </div>

              <form onSubmit={submit} className="mt-6 space-y-3 pt-6 border-t border-brand-line">
                <div className="label-eyebrow text-brand-muted">After paying, submit reference</div>
                <div>
                  <Label className="text-xs text-brand-muted">UPI Transaction ID / UTR</Label>
                  <Input value={txn} onChange={(e) => setTxn(e.target.value)} required data-testid="upi-txn-input" className="mt-1 h-11 rounded-none border-brand-line" placeholder="e.g. 402316783452" />
                </div>
                <div>
                  <Label className="text-xs text-brand-muted">Your name on payment (optional)</Label>
                  <Input value={payer} onChange={(e) => setPayer(e.target.value)} data-testid="upi-payer-input" className="mt-1 h-11 rounded-none border-brand-line" />
                </div>
                <div>
                  <Label className="text-xs text-brand-muted">Note (optional)</Label>
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="upi-note-input" className="rounded-none border-brand-line" />
                </div>
                <Button type="submit" disabled={submitting} data-testid="upi-submit-btn" className="w-full h-12 rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white">
                  {submitting ? "Submitting…" : "I've paid — submit reference"}
                </Button>
                <p className="text-[11px] text-brand-muted leading-relaxed">
                  Membership activates after our team verifies your payment (typically within 24 hours). You'll get a confirmation email.
                </p>
              </form>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CopyRow({ label, value, onCopy, copied, icon: Icon, testid }) {
  return (
    <div className="flex items-center justify-between border border-brand-line bg-white px-4 py-3">
      <div>
        <div className="label-eyebrow text-brand-muted text-[10px]">{label}</div>
        <div className="flex items-center gap-1.5 mt-0.5 text-brand-ink font-medium">
          {Icon && <Icon size={14} className="text-brand-terracotta" />}
          {value}
        </div>
      </div>
      <button type="button" onClick={onCopy} data-testid={testid} className="p-2 hover:bg-brand-bone rounded-full">
        {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} className="text-brand-muted" />}
      </button>
    </div>
  );
}
